import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout, { StatusBadge } from "../../components/admin/AdminLayout";
import { listCampaigns, deleteCampaign } from "../../lib/admin-api";
import { Plus, Eye, Edit, Trash2, Send, Calendar } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
    draft: { label: "Borrador", color: "bg-neutral-100 text-neutral-600 border-neutral-200" },
    scheduled: { label: "Programada", color: "bg-amber-100 text-amber-700 border-amber-200" },
    sending: { label: "Enviando", color: "bg-blue-100 text-blue-700 border-blue-200" },
    sent: { label: "Enviada", color: "bg-green-100 text-green-700 border-green-200" },
    failed: { label: "Fallida", color: "bg-red-100 text-red-700 border-red-200" },
};

export default function AdminCampaigns() {
    const [items, setItems] = useState([]);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        let alive = true;
        listCampaigns().then((d) => { if (alive) setItems(d.items || []); }).catch(console.error);
        return () => { alive = false; };
    }, [refresh]);

    const handleDelete = async (c) => {
        if (!window.confirm(`¿Eliminar campaña "${c.name}"?`)) return;
        try {
            await deleteCampaign(c.id);
            toast.success("Campaña eliminada");
            setRefresh((r) => r + 1);
        } catch (e) {
            toast.error("Error al eliminar");
        }
    };

    return (
        <AdminLayout title="Campañas de email" subtitle="Crea, programa y envía campañas a tus suscriptores" testid="admin-campaigns-page">
            <div className="flex justify-end">
                <Link to="/admin/campaigns/new" className="h-10 px-4 bg-eclipse-cta text-white text-sm font-semibold rounded-md flex items-center gap-2 hover:bg-orange-600" data-testid="campaign-create-btn">
                    <Plus className="w-4 h-4" /> Nueva campaña
                </Link>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Nombre</th>
                                <th className="text-left px-4 py-3">Asunto A</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Enviados</th>
                                <th className="text-left px-4 py-3">Fallidos</th>
                                <th className="text-left px-4 py-3">Programada</th>
                                <th className="text-left px-4 py-3">Enviada</th>
                                <th className="text-right px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Aún no hay campañas. Crea la primera.</td></tr>}
                            {items.map((c) => {
                                const st = STATUS_COLORS[c.status] || { label: c.status, color: "bg-neutral-100" };
                                return (
                                    <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50/40" data-testid={`campaign-row-${c.id}`}>
                                        <td className="px-4 py-3 font-semibold text-neutral-900">{c.name}</td>
                                        <td className="px-4 py-3 text-neutral-700 max-w-xs truncate">{c.subject_a}</td>
                                        <td className="px-4 py-3"><StatusBadge label={st.label} color={st.color} /></td>
                                        <td className="px-4 py-3 text-neutral-700">{c.sent_count || 0}</td>
                                        <td className="px-4 py-3 text-neutral-500">{c.failed_count || 0}</td>
                                        <td className="px-4 py-3 text-neutral-500 text-xs">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString("es-MX") : "—"}</td>
                                        <td className="px-4 py-3 text-neutral-500 text-xs">{c.sent_at ? new Date(c.sent_at).toLocaleString("es-MX") : "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Link to={`/admin/campaigns/${c.id}`} className="p-1.5 hover:text-eclipse-cta" title="Ver" data-testid={`campaign-view-${c.id}`}><Eye className="w-4 h-4" /></Link>
                                                {c.status === "draft" && <Link to={`/admin/campaigns/${c.id}/edit`} className="p-1.5 hover:text-eclipse-cta" title="Editar"><Edit className="w-4 h-4" /></Link>}
                                                {c.status !== "sent" && c.status !== "sending" && (
                                                    <button onClick={() => handleDelete(c)} className="p-1.5 hover:text-red-600 text-neutral-400" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="text-xs text-neutral-500 flex items-center gap-4 px-2">
                <span className="flex items-center gap-1.5"><Send className="w-3 h-3" /> Envío inmediato</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Programación con scheduler automático</span>
            </div>
        </AdminLayout>
    );
}
